import type { FirebaseApp } from 'firebase/app';
import {
  GoogleAuthProvider,
  browserPopupRedirectResolver,
  connectAuthEmulator,
  getAuth,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
  type Auth,
  type UserCredential,
} from 'firebase/auth';

/**
 * Référence interne vers l'instance Auth initialisée.
 *
 * On la mémorise pour que `signInWithEmail` / `signOutTaki` puissent fonctionner
 * sans avoir à repasser l'`app` à chaque appel. `initAuth(app)` doit donc être
 * appelé une fois au démarrage (après `loadTakiConfig` + `createTakiSdk`).
 */
let authInstance: Auth | null = null;

/** Instances Auth déjà branchées sur l'émulateur (branchement une seule fois). */
const emulatorBoundAuths = new WeakSet<Auth>();

/**
 * Adresse de l'émulateur Auth local. Volontairement structurel : `@taki/auth`
 * ne dépend pas de `@taki/runtime-config`, l'hôte lui passe simplement le bloc
 * `config.emulators?.auth`, qui a exactement cette forme.
 */
export interface AuthEmulatorEndpoint {
  host: string;
  port: number;
}

/**
 * Initialise (ou récupère) l'instance Firebase Auth pour l'app donnée.
 *
 * À appeler une fois au boot, en réutilisant le même `FirebaseApp` que celui
 * passé au SDK (`createTakiSdk(cfg).app`), afin de partager une seule instance Firebase.
 *
 * @param app - L'application Firebase déjà initialisée (via `@taki/sdk`).
 * @param emulator - Adresse de l'émulateur Auth local (dev 100% local, cf.
 *   `pnpm dev:local`) : passer `config.emulators?.auth`. Omis ou `undefined`
 *   (cas production) : Auth parle au cloud, comportement inchangé.
 * @returns L'instance `Auth` associée.
 */
export function initAuth(app: FirebaseApp, emulator?: AuthEmulatorEndpoint): Auth {
  authInstance = getAuth(app);

  if (emulator && !emulatorBoundAuths.has(authInstance)) {
    // `disableWarnings` : la bannière "emulator" du SDK n'apporte rien ici, le
    // branchement est explicite et voulu par la config.
    connectAuthEmulator(authInstance, `http://${emulator.host}:${emulator.port}`, {
      disableWarnings: true,
    });
    emulatorBoundAuths.add(authInstance);
    console.info(
      `[@taki/auth] Auth branché sur l'émulateur ${emulator.host}:${emulator.port} (comptes locaux jetables).`,
    );
  }

  return authInstance;
}

/**
 * Récupère l'instance Auth courante, ou lève une erreur explicite si `initAuth`
 * n'a pas encore été appelé.
 */
function requireAuth(): Auth {
  if (!authInstance) {
    throw new Error(
      '[@taki/auth] initAuth(app) doit être appelé avant toute opération d\'authentification.',
    );
  }
  return authInstance;
}

/**
 * Connexion par email / mot de passe (méthode de test du périmètre v0).
 *
 * @param email - Adresse email de l'utilisateur.
 * @param password - Mot de passe.
 * @returns Les identifiants Firebase résultants.
 */
export async function signInWithEmail(
  email: string,
  password: string,
): Promise<UserCredential> {
  return signInWithEmailAndPassword(requireAuth(), email, password);
}

/**
 * CONNEXION PAR UN COMPTE GOOGLE.
 *
 * `signInWithPopup` et non `signInWithRedirect` : la redirection quitte la page,
 * revient par un aller-retour que Safari coupe (son blocage des cookies tiers)
 * et rend le retour d'erreur illisible. La fenetre garde l'app montee et rend
 * l'echec tout de suite.
 *
 * Le resolveur de la fenetre est passe ICI, pas a l'ouverture d'Auth : pose au
 * demarrage, il fait attendre apis.google.com a Safari avant tout ecran
 * (`ouvrirAuth`, `@taki/sdk`). `prechaufferConnexionGoogle` charge gapi avant.
 *
 * `prompt: select_account` a chaque fois, jamais le compte precedent en
 * silence : sur un poste partage, et surtout chez qui a un Gmail perso et un
 * Gmail de travail, entrer avec le mauvais compte sans l'avoir choisi est le
 * defaut le plus penible a comprendre.
 *
 * CE QUE CETTE FONCTION N'ACCORDE PAS : l'acces. Un compte Google valide donne
 * un JETON, pas un droit. C'est la porte (`rattachement.ts`, la callable
 * `rattacherCompte`) qui regarde si l'adresse est invitee dans cette
 * organisation, et elle seule.
 */
export async function signInWithGoogle(): Promise<UserCredential> {
  const provider = new GoogleAuthProvider();
  provider.setCustomParameters({ prompt: 'select_account' });
  return signInWithPopup(requireAuth(), provider, browserPopupRedirectResolver);
}

/** Le script de Google que Firebase charge avant d'ouvrir sa fenetre (meme URL). */
const GAPI_URL = 'https://apis.google.com/js/api.js';

/** Fenetre de chargement de `gapi.iframes`, celle que Firebase se donne. */
const GAPI_TIMEOUT_MS = 30000;

/** Lancee une seule fois par page : React StrictMode double les effets. */
let prechauffageLance = false;

interface GapiFenetre {
  gapi?: {
    load?: (module: string, opts: { callback: () => void; timeout: number; ontimeout: () => void }) => void;
    iframes?: { Iframe?: unknown };
  };
}

/**
 * PRECHAUFFE LA FENETRE GOOGLE, a appeler a l'affichage de l'ecran de connexion.
 *
 * Avant d'ouvrir sa fenetre, Firebase charge gapi (`api.js` puis
 * `gapi.iframes`), et saute ce chargement s'il est deja fait. Or Safari bloque
 * une fenetre ouverte trop longtemps apres le clic. Firebase prechauffait donc
 * au demarrage, ce qui faisait attendre tout l'ecran (`ouvrirAuth`,
 * `@taki/sdk`) ; on prechauffe ici, ou cela ne retarde rien.
 *
 * Silencieuse : un echec ne coute rien, Firebase recharge lui-meme au clic.
 * Sans effet hors navigateur.
 */
export function prechaufferConnexionGoogle(): void {
  if (prechauffageLance || typeof window === 'undefined' || typeof document === 'undefined') return;
  prechauffageLance = true;
  const fenetre = window as unknown as GapiFenetre;
  if (fenetre.gapi?.iframes?.Iframe) return;

  const chargerIframes = (): void => {
    try {
      fenetre.gapi?.load?.('gapi.iframes', {
        callback: () => undefined,
        timeout: GAPI_TIMEOUT_MS,
        ontimeout: () => undefined,
      });
    } catch {
      // Silencieux : Firebase recharge au clic.
    }
  };

  if (fenetre.gapi?.load) {
    chargerIframes();
    return;
  }
  const script = document.createElement('script');
  script.async = true;
  script.src = GAPI_URL;
  script.onload = chargerIframes;
  script.onerror = () => undefined;
  document.head.appendChild(script);
}

/**
 * Déconnexion de l'utilisateur courant.
 */
export async function signOutTaki(): Promise<void> {
  return signOut(requireAuth());
}

/**
 * Accès optionnel à l'instance Auth (utile au hook `useUser`).
 * Retourne `null` tant que `initAuth` n'a pas été appelé.
 */
export function getAuthInstance(): Auth | null {
  return authInstance;
}

/**
 * L'authentification parle-t-elle a un EMULATEUR local, ici et maintenant ?
 *
 * C'est le verrou de la connexion locale sans saisie (cf. le bloc
 * `emulators.connexion` de `@taki/runtime-config`). Un fichier de config peut
 * porter un compte de test ; ce n'est pas une raison suffisante pour entrer
 * seul. Il faut EN PLUS que l'instance Auth soit reellement branchee sur un
 * emulateur, ce qui ne se produit qu'apres `connectAuthEmulator`, et donc
 * jamais en production. Les deux verrous sont independants : ni la config ni le
 * code ne peuvent, seuls, ouvrir la porte.
 *
 * Faux si `initAuth` n'a pas encore ete appele : rien a interroger.
 */
export function isAuthOnEmulator(): boolean {
  if (!authInstance) {
    return false;
  }
  // Source la plus sure : Firebase pose `emulatorConfig` sur l'instance des que
  // `connectAuthEmulator` a ete appele, y compris si le branchement a ete fait
  // ailleurs que par `initAuth`.
  if (authInstance.emulatorConfig !== null) {
    return true;
  }
  // Repli sur le registre que ce fichier tient deja, pour une version du SDK
  // Firebase qui n'exposerait pas la propriete.
  return emulatorBoundAuths.has(authInstance);
}
